import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCartQuery } from '@/features/cart/cartApi';
import type { ValidatedBranding } from '@/theme/branding';
import { CustomerIcon } from './CustomerIcon';

interface CustomerHeaderProps {
  branding: ValidatedBranding;
  storeCode?: string;
}

export function CustomerHeader({ branding, storeCode }: CustomerHeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const cart = useCartQuery();
  const count = cart.data?.items?.reduce((sum, item) => sum + (item.quantity ?? 0), 0) ?? 0;
  const menuPath = storeCode ? `/store/${encodeURIComponent(storeCode)}/menus` : '/menu';
  const links = [
    { label: 'THỰC ĐƠN', to: menuPath, current: location.pathname.includes('/menu') },
    { label: 'MOOD HÔM NAY', to: '/moods', current: location.pathname.startsWith('/moods') },
    { label: 'CỬA HÀNG', to: '/stores', current: location.pathname === '/stores' },
    {
      label: 'NHƯỢNG QUYỀN',
      to: location.pathname.startsWith('/store/') ? '/#franchise' : '/franchise',
      current: location.pathname === '/franchise',
    },
    { label: 'VỀ THIÊN MỘC TC', to: '/about', current: location.pathname === '/about' },
  ];

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen && !searchOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        setSearchOpen(false);
      }
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [menuOpen, searchOpen]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mobile = window.matchMedia('(max-width: 900px)');
    const closeAtDesktop = (event: MediaQueryListEvent) => {
      if (!event.matches) setMenuOpen(false);
    };
    mobile.addEventListener('change', closeAtDesktop);
    return () => mobile.removeEventListener('change', closeAtDesktop);
  }, []);

  return (
    <header className="customer-header">
      <Link className="customer-brand" to="/" aria-label="Dream Up, trang chủ">
        {branding.logoUrl ? (
          <img className="brand-logo" src={branding.logoUrl} width="46" height="46" alt="" />
        ) : (
          <img
            className="brand-logo brand-logo-default"
            src="/brand/dream-up-logo-96.png"
            width="46"
            height="46"
            alt=""
          />
        )}
        <span className="brand-copy">
          <strong>Dream Up</strong>
          <small>PREMIUM CACAO</small>
        </span>
      </Link>
      <nav
        id="customer-header-navigation"
        className={`customer-desktop-nav${menuOpen ? ' is-open' : ''}`}
        aria-label="Điều hướng cửa hàng"
      >
        {links.map((link) => (
          <Link
            key={link.label}
            to={link.to}
            aria-current={link.current ? 'page' : undefined}
            onClick={() => setMenuOpen(false)}
          >
            {link.label}
          </Link>
        ))}
      </nav>
      <button
        className="customer-menu-toggle"
        type="button"
        aria-label={menuOpen ? 'Đóng menu' : 'Mở menu'}
        aria-controls="customer-header-navigation"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((current) => !current)}
      >
        {menuOpen ? (
          <span className="customer-menu-close" aria-hidden="true">
            ×
          </span>
        ) : (
          <CustomerIcon name="menu" />
        )}
      </button>
      <div className="customer-header-actions">
        <button
          className="header-search-button"
          type="button"
          aria-label="Tìm kiếm hoặc theo dõi đơn"
          aria-haspopup="dialog"
          aria-expanded={searchOpen}
          onClick={() => setSearchOpen(true)}
        >
          <CustomerIcon name="search" />
        </button>
        <Link className="header-search" to="/search" aria-label="Tìm kiếm món">
          <CustomerIcon name="search" />
        </Link>
      <Link className="header-cart" to="/cart" aria-label={`Giỏ hàng, ${count} món`}>
        <span className="header-cart-icon">
          <CustomerIcon name="cart" />
        </span>
        <span className="header-cart-label">Giỏ hàng</span>
        {count > 0 && <span className="header-cart-count">{count}</span>}
      </Link>
      </div>
      {searchOpen && (
        <HeaderSearchDialog
          onClose={() => setSearchOpen(false)}
          onNavigate={(to) => navigate(to)}
        />
      )}
    </header>
  );
}

function HeaderSearchDialog({
  onClose,
  onNavigate,
}: {
  onClose: () => void;
  onNavigate: (to: string) => void;
}) {
  const [mode, setMode] = useState<'products' | 'tracking'>('products');
  const [value, setValue] = useState('');
  const isTracking = mode === 'tracking';

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const input = value.trim();
    if (!input) return;
    onClose();
    onNavigate(
      isTracking
        ? `/track/${encodeURIComponent(input)}`
        : `/search?q=${encodeURIComponent(input)}`
    );
  };

  return (
    <div className="header-search-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="header-search-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="header-search-dialog-heading"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="header-search-dialog-heading">
          <div>
            <p>Tìm nhanh</p>
            <h2 id="header-search-dialog-heading">Bạn muốn làm gì?</h2>
          </div>
          <button type="button" aria-label="Đóng" onClick={onClose}>
            <CustomerIcon name="close" />
            ×
          </button>
        </div>
        <div className="header-search-mode" role="radiogroup" aria-label="Loại tìm kiếm">
          <button
            type="button"
            role="radio"
            aria-checked={!isTracking}
            onClick={() => setMode('products')}
          >
            <CustomerIcon name="search" />
            <span>Tìm món</span>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={isTracking}
            onClick={() => setMode('tracking')}
          >
            <CustomerIcon name="cart" />
            <span>Theo dõi đơn</span>
          </button>
        </div>
        <form onSubmit={submit}>
          <label>
            <span>{isTracking ? 'Mã đơn hàng' : 'Tên món'}</span>
            <input
              type={isTracking ? 'text' : 'search'}
              value={value}
              onChange={(event) => setValue(event.target.value)}
              placeholder={isTracking ? 'Ví dụ: FNB-20260726-ABC123' : 'Nhập tên món...'}
              autoFocus
              autoComplete="off"
            />
          </label>
          {isTracking && (
            <p className="header-search-tracking-note">
              Đơn chỉ hiển thị khi thiết bị này còn quyền theo dõi hợp lệ.
            </p>
          )}
          <button className="header-search-submit" type="submit" disabled={!value.trim()}>
            {isTracking ? 'Theo dõi đơn' : 'Tìm món'}
          </button>
        </form>
      </section>
    </div>
  );
}
